import {
  btnDanger,
  btnPrimary,
  btnSecondary
} from '@/components/record-sheet/styles'
import { ApiError } from '@/lib/api'
import { type ChatCard, type ChatMessage, decideCard } from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { deleteMemory } from '@/lib/team-memory'
import { ToolLogo } from '@/lib/tool-icons'
import { cn } from '@/lib/utils'
import { Brain, Check, Loader2, RotateCcw, X } from 'lucide-react'
import { Fragment, useState } from 'react'
import { toast } from 'sonner'
import { AgentAvatar, useFresh } from './AgentAvatar'
import { AgentText } from './ChatWork'
import { ChatWorkflowCard } from './workflows/ChatWorkflowCard'
import { DataWidget } from './DataWidget'
import { ChatBuildQuestions } from './workflows/BuildQuestions'
import { focusDecision } from './decisions'
import { ClassifyMatches } from './workflows/ClassifyMatches'

const time = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

export function MessageRow({
  message,
  roomId,
  continued = false
}: {
  message: ChatMessage
  roomId: number
  /** The answer right under this agent's own steps: the steps line already shows who it is. */
  continued?: boolean
}) {
  const m = message
  if (m.role === 'admin') {
    return (
      <div className="group flex flex-col items-end gap-1">
        <div className="max-w-[75%] rounded-[36px] bg-neutral-100 px-4 py-2.5 text-[15px] leading-relaxed text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100">
          <p className="whitespace-pre-wrap">{m.text}</p>
        </div>
        <p className="px-2 text-[11px] text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100">
          {m.data.to ? `To ${m.data.to} · ` : ''}
          {time(m.created_at)}
        </p>
      </div>
    )
  }

  if (m.role === 'system' && m.data.workflow_card) {
    return (
      <ChatWorkflowCard id={m.data.workflow_card} prompt={m.text} refine={!!m.data.refine} />
    )
  }

  if (m.role === 'system' && m.data.memory) {
    return <LearnedLine message={m} />
  }

  if (m.role === 'system') {
    return (
      <p className="text-center text-xs text-neutral-500 dark:text-neutral-400">
        {m.text}
      </p>
    )
  }

  // Waiting cards are decided in the bar above the chat box; here they're one line that points there.
  if (m.role === 'approval' && m.card && m.card.status === 'open') {
    return <WaitingLine message={m} />
  }

  if (m.role === 'approval' && m.card) {
    return (
      <div>
        <ActionCard
          card={m.card}
          agentName={m.agent?.name ?? ''}
          roomId={roomId}
        />
      </div>
    )
  }

  return (
    <div className={cn('group', continued && '-mt-3')}>
      <div
        className={cn(
          'mb-1.5 flex items-center gap-2 text-sm',
          continued && 'hidden'
        )}
      >
        <FreshAvatar
          agentKey={m.agent?.key}
          name={m.agent?.name}
          createdAt={m.created_at}
        />
        <span className="font-semibold text-neutral-900 dark:text-white">
          {m.agent?.name}
        </span>
        {m.data.routine && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
            Routine · {m.data.routine}
          </span>
        )}
        {m.data.background && m.data.trigger && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
            {m.data.trigger === 'Schedule' ? 'Scheduled check' : m.data.trigger}
          </span>
        )}
        <span className="text-xs text-neutral-400 opacity-0 transition-opacity group-hover:opacity-100">
          {time(m.created_at)}
        </span>
      </div>
      {m.data.widget ? (
        <DataWidget widget={m.data.widget} />
      ) : m.data.classify ? (
        <ClassifyMatches messageId={m.id} data={m.data.classify} />
      ) : m.data.workflow_questions ? (
        <div className="space-y-2">
          <div className="text-[15px] leading-7 text-neutral-800 dark:text-neutral-200">
            <AgentText text={m.text} />
          </div>
          <ChatBuildQuestions id={m.data.workflow_questions} />
        </div>
      ) : (
        <div className="text-[15px] leading-7 text-neutral-800 dark:text-neutral-200">
          <AgentText text={m.text} />
        </div>
      )}
    </div>
  )
}

const STATUS_TONE: Record<ChatCard['status'], string> = {
  open: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  approved:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  answered:
    'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  rejected:
    'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300',
  failed: 'bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400'
}

export function ActionCard({
  card,
  agentName,
  roomId
}: { card: ChatCard; agentName: string; roomId?: number | null }) {
  const [note, setNote] = useState('')
  const [pending, setPending] = useState<string | null>(null)
  const [picks, setPicks] = useState<Record<number, string[]>>({})
  const [remember, setRemember] = useState(false)
  const open = card.status === 'open'
  // Only where a standing rule is allowed (questions, small reversible actions; never money).
  const canRemember = !!card.can_remember
  // A note on an approval means "not like this": it goes back to the agent instead of approving as-is.
  const sendingBack = card.kind === 'approval' && note.trim().length > 0
  const hasChoices = card.questions.length > 0

  function pick(question: number, option: string, multi: boolean) {
    setPicks((all) => {
      const current = all[question] ?? []
      const next = multi
        ? current.includes(option)
          ? current.filter((o) => o !== option)
          : [...current, option]
        : current[0] === option
          ? []
          : [option]
      return { ...all, [question]: next }
    })
  }

  // Taps and the optional note become one plain answer the agent reads.
  const answer = hasChoices
    ? [
        ...card.questions
          .map((q, i) =>
            picks[i]?.length ? `${q.question} ${picks[i].join(', ')}` : ''
          )
          .filter(Boolean),
        note.trim()
      ]
        .filter(Boolean)
        .join('\n')
    : note

  async function decide(
    decision: 'approve' | 'reject' | 'answer' | 'retry',
    text = note
  ) {
    setPending(decision)
    try {
      const result = await decideCard(card.id, decision, text, remember && canRemember)
      if (result.status === 'failed')
        toast.error('Approved, but it didn’t work', { description: result.error || undefined })
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['ai-room', roomId] }),
        queryClient.invalidateQueries({ queryKey: ['ai-approvals'] }),
        queryClient.invalidateQueries({ queryKey: ['ai-rooms'] })
      ])
      setNote('')
      setPicks({})
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Decided somewhere else meanwhile (another tab, the bar, an email): show what happened.
        toast(err.message)
        queryClient.invalidateQueries({ queryKey: ['ai-room', roomId] })
        queryClient.invalidateQueries({ queryKey: ['ai-approvals'] })
      } else notifyError('Could not send your decision', err)
    } finally {
      setPending(null)
    }
  }

  return (
    <div
      className={cn(
        'w-full space-y-3 rounded-[36px] border bg-white p-5 dark:bg-neutral-900',
        open
          ? card.money || card.severity === 'urgent'
            ? 'border-red-200 dark:border-red-900/50'
            : 'border-amber-200 dark:border-amber-500/30'
          : 'border-neutral-200 dark:border-neutral-800'
      )}
    >
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <AgentAvatar
          name={agentName}
          size="xs"
          state={open ? 'waiting' : 'idle'}
        />
        <span className="font-semibold text-neutral-900 dark:text-white">
          {agentName}
        </span>
        <span className="text-neutral-500 dark:text-neutral-400">
          {card.kind === 'approval'
            ? 'needs your yes'
            : card.kind === 'question'
              ? 'has a question'
              : 'wants you to know'}
        </span>
        <span
          className={cn(
            'ml-auto rounded-full px-2.5 py-0.5 font-semibold',
            STATUS_TONE[card.status]
          )}
        >
          {card.status_label}
        </span>
      </div>

      <p className="flex items-center gap-2 font-bold text-neutral-900 dark:text-white">
        {card.toolkit && <ToolLogo toolkit={card.toolkit} size="md" />}
        {card.title}
      </p>
      {card.why && (
        <p className="whitespace-pre-wrap text-sm text-neutral-600 dark:text-neutral-300">
          {card.why}
        </p>
      )}

      {card.details.length > 0 && (
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          {card.details.map((d) => (
            <Fragment key={d.label}>
              <dt className="text-neutral-500 dark:text-neutral-400">
                {d.label}
              </dt>
              <dd className="break-words text-neutral-900 dark:text-white">
                {d.value}
              </dd>
            </Fragment>
          ))}
        </dl>
      )}

      {card.draft && (
        <div className="rounded-[36px] bg-neutral-50 px-4 py-3 text-sm text-neutral-700 dark:bg-neutral-800/60 dark:text-neutral-200">
          <p className="whitespace-pre-wrap">{card.draft}</p>
        </div>
      )}

      {card.price && (
        <div
          className={cn(
            'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-[36px] px-4 py-2.5',
            open
              ? 'bg-amber-50 dark:bg-amber-500/10'
              : 'bg-neutral-50 dark:bg-neutral-800/60'
          )}
        >
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {open ? 'Costs' : 'Cost'}
          </span>
          <span className="text-lg font-bold tabular-nums text-neutral-900 dark:text-white">
            {money(card.price.amount)}
          </span>
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            {card.price.label}
          </span>
        </div>
      )}

      {(card.images.length > 0 || card.generating) && (
        <CardImages card={card} />
      )}

      {card.money && open && (
        <p className="text-xs font-semibold text-red-600 dark:text-red-400">
          This moves money or can't be undone.
        </p>
      )}
      {card.error && (
        <div className="space-y-2">
          <p className="text-sm text-red-600 dark:text-red-400">
            It failed: {card.error}
          </p>
          {card.retryable && (
            <button
              type="button"
              className={btnSecondary}
              disabled={!!pending}
              onClick={() => decide('retry', '')}
            >
              {pending === 'retry' ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <RotateCcw className="size-4" />
              )}
              Try again
              {card.price?.amount ? ` · ${money(card.price.amount)}` : ''}
            </button>
          )}
        </div>
      )}
      {!open && (card.answer || card.decided_by) && (
        <p className="whitespace-pre-line text-xs text-neutral-500 dark:text-neutral-400">
          {card.decided_by && `By ${card.decided_by}`}
          {card.answer && ` · "${card.answer}"`}
        </p>
      )}

      {open && card.questions.length > 0 && (
        <div className="space-y-3">
          {card.questions.map((q, qi) => (
            <fieldset key={q.question} className="space-y-1.5">
              <legend className="text-sm font-semibold text-neutral-900 dark:text-white">
                {q.question}
                {q.multi && (
                  <span className="ml-1.5 font-normal text-neutral-400">
                    pick any
                  </span>
                )}
              </legend>
              <div className="flex flex-wrap gap-1.5">
                {q.options.map((option) => {
                  const on = (picks[qi] ?? []).includes(option)
                  return (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={on}
                      disabled={!!pending}
                      onClick={() => pick(qi, option, q.multi)}
                      className={cn(
                        'rounded-full border px-3.5 py-1.5 text-sm transition-all active:scale-95 disabled:opacity-50',
                        on
                          ? 'border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-black'
                          : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-200 dark:hover:border-neutral-600'
                      )}
                    >
                      {option}
                    </button>
                  )
                })}
              </div>
            </fieldset>
          ))}
        </div>
      )}

      {open && (
        <div className="space-y-2">
          {card.kind !== 'alert' && (
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={card.kind === 'question' && !hasChoices ? 2 : 1}
              placeholder={
                hasChoices
                  ? 'Anything else? (optional)'
                  : card.kind === 'question'
                    ? 'Your answer…'
                    : 'Want it changed? Write how (it goes back to the agent)'
              }
              aria-label="Note for the agent"
              className="w-full resize-none rounded-[36px] border border-neutral-200 bg-white px-4 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-400 dark:border-neutral-800 dark:bg-neutral-950 dark:text-white"
            />
          )}
          <div className="flex flex-wrap gap-2">
            {card.kind === 'approval' &&
              (sendingBack ? (
                  <button
                    type="button"
                    className={btnPrimary}
                    disabled={!!pending}
                    onClick={() => decide('reject')}
                    title="Not as it is: your note goes back to the agent"
                  >
                    {pending === 'reject' ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : (
                      <RotateCcw className="size-4" />
                    )}
                    Send back with changes
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      className={btnPrimary}
                      disabled={!!pending}
                      onClick={() => decide('approve')}
                    >
                      {pending === 'approve' ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                      {card.price?.amount
                        ? `Approve · ${money(card.price.amount)}`
                        : 'Approve'}
                    </button>
                    <button
                      type="button"
                      className={btnDanger}
                      disabled={!!pending}
                      onClick={() => decide('reject')}
                    >
                      {pending === 'reject' ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <X className="size-4" />
                      )}
                      Say no
                    </button>
                  </>
                ))}
            {card.kind === 'question' && (
              <button
                type="button"
                className={btnPrimary}
                disabled={!!pending || !answer.trim()}
                onClick={() => decide('answer', answer)}
              >
                {pending && <Loader2 className="size-4 animate-spin" />}
                Answer
              </button>
            )}
            {card.kind === 'alert' && (
              <button
                type="button"
                className={btnSecondary}
                disabled={!!pending}
                onClick={() => decide('answer', '')}
              >
                {pending && <Loader2 className="size-4 animate-spin" />}
                Got it
              </button>
            )}
          </div>
          {canRemember && (
            <label className="flex cursor-pointer items-center gap-2 px-1 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
              <input
                type="checkbox"
                className="size-3.5 accent-neutral-900 dark:accent-white"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              {card.kind === 'approval'
                ? "Don't ask me this again (approve: it just happens next time; say no: never suggested again)"
                : "Don't ask me this again: the whole team remembers my answer"}
            </label>
          )}
          {card.kind === 'approval' && card.money && (
            <p className="px-1 text-[11px] text-neutral-400">Anything involving money always asks you.</p>
          )}
        </div>
      )}
    </div>
  )
}

/** An agent's orb "replies" (smiles) for a few seconds after its answer arrives, then settles. */
function FreshAvatar({
  agentKey,
  name,
  createdAt
}: { agentKey?: string; name?: string; createdAt: string }) {
  const fresh = useFresh(createdAt)
  return (
    <AgentAvatar
      agentKey={agentKey}
      name={name}
      size="xs"
      state={fresh ? 'replying' : 'idle'}
    />
  )
}

const money = (amount: number | null) =>
  amount == null
    ? 'Budget not set'
    : `$${amount.toLocaleString(undefined, {
        minimumFractionDigits: amount < 10 ? 2 : 0,
        maximumFractionDigits: 2
      })}`

/** Generated images, or placeholders while they're being made. */
function CardImages({ card }: { card: ChatCard }) {
  const count = Number(
    card.details.find((d) => d.label === 'Count')?.value ?? 1
  )
  if (card.generating) {
    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {Array.from({ length: Math.max(1, Math.min(count || 1, 4)) }).map(
            (_, i) => (
              <div
                key={i}
                className="aspect-square animate-pulse rounded-[36px] bg-neutral-200 dark:bg-neutral-800"
              />
            )
          )}
        </div>
        <p className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
          <Loader2 className="size-4 animate-spin" />
          Making the images… about 20 seconds each.
        </p>
      </div>
    )
  }
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {card.images.map((url, i) => (
        <a
          key={url}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="group/img block overflow-hidden rounded-[36px] border border-neutral-200 bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-950"
        >
          <img
            src={url}
            alt={`Generated image ${i + 1} for ${card.title}`}
            loading="lazy"
            className="aspect-square w-full object-cover transition-transform duration-300 group-hover/img:scale-105"
          />
        </a>
      ))}
    </div>
  )
}

/** "Learned: …" in the chat when the team saves something, with Undo right there. */
function LearnedLine({ message }: { message: ChatMessage }) {
  const [undone, setUndone] = useState(!!message.data.undone)
  const [busy, setBusy] = useState(false)
  async function undo() {
    if (!message.data.memory) return
    setBusy(true)
    try {
      await deleteMemory(message.data.memory)
      setUndone(true)
      queryClient.invalidateQueries({ queryKey: ['team-memory'] })
    } catch (err) {
      notifyError('Could not undo it', err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <div className="flex justify-center">
      <span
        className={cn(
          'inline-flex max-w-full items-center gap-2 rounded-full border border-neutral-200 bg-white py-1 pl-3 pr-1.5 text-xs dark:border-neutral-800 dark:bg-neutral-900',
          undone && 'opacity-60'
        )}
      >
        <Brain className="size-3.5 shrink-0 text-neutral-500 dark:text-neutral-400" />
        <span className={cn('min-w-0 truncate text-neutral-700 dark:text-neutral-200', undone && 'line-through')}>
          {message.text}
        </span>
        {undone ? (
          <span className="shrink-0 pr-1.5 font-semibold text-neutral-400">Undone</span>
        ) : (
          <button
            type="button"
            onClick={undo}
            disabled={busy}
            className="shrink-0 rounded-full px-2 py-0.5 font-semibold text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50 dark:text-neutral-300 dark:hover:bg-neutral-800"
          >
            {busy ? <Loader2 className="size-3 animate-spin" /> : 'Undo'}
          </button>
        )}
      </span>
    </div>
  )
}

/** A card still waiting on the owner, as one line in the conversation; Decide brings it up in the bar. */
function WaitingLine({ message }: { message: ChatMessage }) {
  const card = message.card as ChatCard
  return (
    <div className="flex justify-center">
      <button
        type="button"
        onClick={() => focusDecision(card.id)}
        className="inline-flex max-w-full items-center gap-2 rounded-full border border-amber-200 bg-amber-50 py-1 pl-1 pr-3 text-left text-xs transition-colors hover:border-amber-300 dark:border-amber-500/30 dark:bg-amber-500/10 dark:hover:border-amber-500/50"
      >
        <AgentAvatar agentKey={message.agent?.key} name={message.agent?.name} size="xs" />
        <span className="min-w-0 truncate text-amber-900 dark:text-amber-200">
          <span className="font-semibold">{message.agent?.name ?? 'The team'}</span> is waiting on you: {card.title}
        </span>
        <span className="shrink-0 font-bold text-amber-800 dark:text-amber-300">Decide ↓</span>
      </button>
    </div>
  )
}
